import { SidebarLogo } from '@/components/BrandLogo'
import { TenantSwitcher } from '@/components/TenantSwitcher'
import { UserProfileMenu } from '@/components/UserProfileMenu'
import { useTenant } from '@/contexts/TenantContext'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import { Boxes, ChevronsLeft, ChevronsRight, FolderKanban, Settings, Users } from 'lucide-react'
import { NavLink } from 'react-router-dom'

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { currentTenant } = useTenant()
  const { hasPermission, isAdmin } = useAuth()
  const isTenantAdmin = currentTenant?.role === 'Admin'
  const canManageTenantSettings =
    isAdmin || hasPermission(Permissions.Host.Tenant.Settings.Manage)
  const canViewSandboxImages =
    isAdmin || hasPermission(Permissions.Host.SandboxImage.List)

  const navItems = [
    { to: '/projects', label: 'Projects', icon: FolderKanban },
    ...(isTenantAdmin ? [{ to: '/tenant-members', label: 'Users', icon: Users }] : []),
    ...(canManageTenantSettings
      ? [{ to: '/tenant/settings', label: 'Settings', icon: Settings }]
      : []),
    ...(canViewSandboxImages
      ? [{ to: '/sandbox-images', label: 'Sandbox Images', icon: Boxes }]
      : []),
  ]

  return (
    <aside className={`sticky top-0 z-20 h-screen shrink-0 ${collapsed ? 'w-16' : 'w-64'} border-r bg-card flex flex-col transition-all duration-200`}>
      {collapsed && (
        <button
          onClick={onToggle}
          className="absolute top-[18px] left-full z-30 ml-1 flex h-7 w-7 items-center justify-center rounded-md border bg-card shadow-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Expand sidebar"
        >
          <ChevronsRight className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex h-16 items-center border-b px-4">
        <SidebarLogo collapsed={collapsed} />
        {!collapsed && (
          <button
            onClick={onToggle}
            className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Collapse sidebar"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>
      <TenantSwitcher collapsed={collapsed} />
      <nav className="flex-1 space-y-1 p-3">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'} rounded-md py-2 text-sm font-medium transition-colors ${isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && item.label}
          </NavLink>
        ))}
      </nav>
      <UserProfileMenu collapsed={collapsed} />
    </aside>
  )
}
